import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { apiError } from '../../../../shared/api.js';
import { useRules } from '../../hooks/useRules.js';
import { usePackages } from '../../hooks/usePackages.js';
import { useDiscounts } from '../../hooks/useDiscounts.js';
import { useSimulate } from '../../hooks/useSimulate.js';
import { BreakdownDisplay } from './BreakdownDisplay.jsx';

const TYPES = [
  { key: 'hourly', label: 'ساعي' },
  { key: 'package', label: 'باقة' },
  { key: 'fixed', label: 'مبلغ ثابت' },
  { key: 'open', label: 'وقت مفتوح' }
];

function todayAt(hour, minute = 0) {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d;
}

function toIso(d) {
  if (!d) return null;
  return new Date(d).toISOString();
}

function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function Simulator() {
  const rulesQuery = useRules();
  const packagesQuery = usePackages();
  const discountsQuery = useDiscounts();
  const simulate = useSimulate();

  const rules = rulesQuery.data || [];
  const packages = packagesQuery.data || [];
  const discounts = discountsQuery.data || [];

  const defaultRule = useMemo(() => rules.find((r) => r.isDefault) || rules[0] || null, [rules]);
  const defaultPkg = packages[0] || null;

  const now = new Date();
  const plus2h = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  const [startTime, setStartTime] = useState(toLocalInputValue(now));
  const [endTime, setEndTime] = useState(toLocalInputValue(plus2h));
  const [pricingType, setPricingType] = useState('hourly');
  const [ruleId, setRuleId] = useState(defaultRule?._id || defaultRule?.id || '');
  const [packageId, setPackageId] = useState(defaultPkg?._id || defaultPkg?.id || '');
  const [fixedAmount, setFixedAmount] = useState(15000);
  const [openAmount, setOpenAmount] = useState(30000);
  const [siblingsIndex, setSiblingsIndex] = useState(0);
  const [discountIds, setDiscountIds] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // hydrate defaults once data lands
  useEffect(() => {
    if (!ruleId && defaultRule) setRuleId(defaultRule._id || defaultRule.id);
    if (!packageId && defaultPkg) setPackageId(defaultPkg._id || defaultPkg.id);
  }, [defaultRule, defaultPkg]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleDiscount(id) {
    setDiscountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function runSimulate() {
    setError(null);
    const body = {
      startTime: toIso(startTime),
      endTime: toIso(endTime),
      pricingType,
      siblingsIndex: Number(siblingsIndex) || 0,
      isHoliday: !!isHoliday,
      discountIds
    };
    if (pricingType === 'hourly') body.ruleId = ruleId || undefined;
    if (pricingType === 'package') body.packageId = packageId || undefined;
    if (pricingType === 'fixed') body.fixedAmount = Number(fixedAmount) || 0;
    if (pricingType === 'open') body.openAmount = Number(openAmount) || 0;

    try {
      const data = await simulate.mutateAsync(body);
      setResult(data);
    } catch (err) {
      setError(apiError(err));
      setResult(null);
    }
  }

  return (
    <div className="grid gap-3.5" style={{ gridTemplateColumns: '1fr 1fr' }}>
      <Card header="إعدادات المحاكاة" sub="جرّب أي قاعدة قبل تطبيقها">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">وقت الدخول</label>
              <input
                className="input"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">وقت الخروج</label>
              <input
                className="input"
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">نوع التسعير</label>
            <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setPricingType(t.key)}
                  className={clsx(
                    'flex-1 px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                    pricingType === t.key
                      ? 'bg-orange text-white shadow-orange-glow'
                      : 'text-ink-2 hover:bg-white'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {pricingType === 'hourly' ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">القاعدة</label>
                <select
                  className="input"
                  value={ruleId}
                  onChange={(e) => setRuleId(e.target.value)}
                >
                  <option value="">— اختر قاعدة —</option>
                  {rules.map((r) => (
                    <option key={r._id || r.id} value={r._id || r.id}>
                      {r.name}{r.isDefault ? ' (افتراضية)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
                <input
                  type="checkbox"
                  className="accent-orange"
                  checked={isHoliday}
                  onChange={(e) => setIsHoliday(e.target.checked)}
                />
                يوم عطلة (تطبيق سعر العطلة)
              </label>
            </div>
          ) : null}

          {pricingType === 'package' ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الباقة</label>
              <select
                className="input"
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
              >
                <option value="">— اختر باقة —</option>
                {packages.map((p) => (
                  <option key={p._id || p.id} value={p._id || p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {pricingType === 'fixed' ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ الثابت (د.ع)</label>
              <input
                className="input"
                type="number"
                min="0"
                value={fixedAmount}
                onChange={(e) => setFixedAmount(Number(e.target.value) || 0)}
              />
            </div>
          ) : null}

          {pricingType === 'open' ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ المفتوح (د.ع)</label>
              <input
                className="input"
                type="number"
                min="0"
                value={openAmount}
                onChange={(e) => setOpenAmount(Number(e.target.value) || 0)}
              />
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">عدد الأشقاء (الترتيب)</label>
            <input
              className="input"
              type="number"
              min="0"
              max="5"
              value={siblingsIndex}
              onChange={(e) => setSiblingsIndex(Number(e.target.value) || 0)}
            />
            <div className="text-[11px] text-ink-3 mt-1">
              0 = أول طفل، 1 = الطفل الثاني، وهكذا.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">خصومات اختيارية</label>
            {discounts.length === 0 ? (
              <div className="text-[12px] text-ink-3">لا توجد خصومات معرّفة</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {discounts.map((d) => {
                  const id = d._id || d.id;
                  const active = discountIds.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleDiscount(id)}
                      className={clsx(
                        'px-3 py-1.5 rounded-[10px] text-[12px] font-semibold border transition-colors',
                        active
                          ? 'bg-orange text-white border-orange'
                          : 'bg-white text-ink-2 border-line hover:bg-paper-2'
                      )}
                    >
                      {d.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn btn-primary btn-lg w-full justify-center"
            onClick={runSimulate}
            disabled={simulate.isPending}
          >
            <Icon name="check" size={16} />
            {simulate.isPending ? 'جاري الحساب...' : 'احسب'}
          </button>
        </div>
      </Card>

      <Card header="نتيجة المحاكاة" sub="تفصيل الاحتساب من الخادم">
        <BreakdownDisplay result={result} loading={simulate.isPending} error={error} />
      </Card>
    </div>
  );
}
